/**
 * Builds the class list for the App shell's main-content wrapper.
 *
 * Why this exists: the wrapper's left margin / top padding change in two
 * very different situations, and they must NOT be treated the same way.
 *
 *  1. Sidebar collapse/expand *within* the authenticated shell
 *     (ml-72 ↔ ml-16). This is an intentional, in-context change and
 *     should animate smoothly.
 *
 *  2. Crossing the auth boundary — entering/leaving a "form screen"
 *     (login, onboarding, public marketing). Here the sidebar and header
 *     are added/removed instantly (`v-if`), so animating the margin makes
 *     the centred content glide in from the lower-right corner (the
 *     "float-in" bug) and makes logout/session-expiry feel unstable.
 *     This must be INSTANT.
 *
 * The caller raises `suppressMotion` for one frame whenever `isAuthPage`
 * flips, so boundary crossings are instant while ordinary sidebar
 * collapse toggles keep their smooth transition.
 *
 * @param {object} opts
 * @param {boolean} opts.isAuthPage          Current route is a form/auth/marketing screen.
 * @param {boolean} opts.isAuthenticated     User is logged in (drives header top-padding).
 * @param {boolean} opts.isSidebarCollapsed  Desktop sidebar is collapsed.
 * @param {boolean} opts.suppressMotion      Skip the transition for this render (boundary crossing).
 * @returns {string} space-separated class list for the wrapper.
 */
export function buildShellContentClass({
  isAuthPage,
  isAuthenticated,
  isSidebarCollapsed,
  suppressMotion,
}) {
  // Only the in-shell sidebar collapse animates. Margin transition uses a
  // motion token so it stays in the centralised 120–220ms band.
  const motion = suppressMotion ? '' : 'transition-[margin,padding] duration-200';

  if (isAuthPage) {
    // No chrome → no margin/padding. Keep it instant on the way in/out.
    return motion;
  }

  const topPadding = isAuthenticated ? 'pt-16' : '';
  const leftMargin = isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72';

  return [motion, topPadding, leftMargin].filter(Boolean).join(' ');
}

export default buildShellContentClass;
